import React from "react";

import { AccordionItemProps, ActionItem, IconItems, PlayerProp, PlayerStyle, Positions } from "../../components";
import { ball, board, cone, net, pile, stickLeft, stickRight, textbox, tire, triangleStick } from "../../images";
import { HockeyPlayerLabelType } from "../../types";
import { ToolPaletteTextBoxId, getGlobalToolPane, toolPaletteShadingIds, toolPaletteTextBoxIds } from "../global";

/** ================================
 * Tool Palette Type Definitions
 ================================ */
/** ======== Player ======== */
export const hkToolPalettePlayerIds = [
  "HK_PLAYER_F_1",
  "HK_PLAYER_F_2",
  "HK_PLAYER_F_3",
  "HK_PLAYER_D_1",
  "HK_PLAYER_D_2",
  "HK_PLAYER_G",
  "HK_PLAYER_POSSESSION_F_1",
  "HK_PLAYER_POSSESSION_F_2",
  "HK_PLAYER_POSSESSION_F_3",
  "HK_PLAYER_POSSESSION_D_1",
  "HK_PLAYER_POSSESSION_D_2",
  "HK_PLAYER_POSSESSION_G",
  "HK_PLAYER_FILL_F_1",
  "HK_PLAYER_FILL_F_2",
  "HK_PLAYER_FILL_F_3",
  "HK_PLAYER_FILL_D_1",
  "HK_PLAYER_FILL_D_2",
  "HK_PLAYER_FILL_G",
] as const;
export type HK_ToolPalettePlayerId = (typeof hkToolPalettePlayerIds)[number];
export const isHKPlayerId = (id: HK_ToolPaletteMarkerId): id is HK_ToolPalettePlayerId =>
  hkToolPalettePlayerIds.includes(id as HK_ToolPalettePlayerId);

/** ======== Puck ======== */
export const hkToolPalettePuckIds = ["HK_PUCK"] as const;
export type HK_ToolPalettePuckId = (typeof hkToolPalettePuckIds)[number];
export const isHKPuckId = (id: HK_ToolPaletteMarkerId): id is HK_ToolPalettePuckId =>
  hkToolPalettePuckIds.includes(id as HK_ToolPalettePuckId);

/** ======== Misc ======== */
export const hkToolPaletteMiscIds = [
  "MISC_PUCK",
  "MISC_FLAG",
  "MISC_CONE",
  "MISC_TIRE",
  "MISC_STICK_RIGHT",
  "MISC_STICK_LEFT",
  "MISC_TRIANGLE_STICK",
  "MISC_BOARD",
  "MISC_PUCK_PILE",
  "MISC_NET",
] as const;
export type HK_ToolPaletteMiscId = (typeof hkToolPaletteMiscIds)[number];
export const isHKMiscId = (id: HK_ToolPaletteMarkerId): id is HK_ToolPaletteMiscId =>
  hkToolPaletteMiscIds.includes(id as HK_ToolPaletteMiscId);

/** ======== All Markers ======== */
export const hkToolPaletteMarkerIds = [
  ...hkToolPalettePlayerIds,
  ...hkToolPalettePuckIds,
  ...hkToolPaletteMiscIds,
  ...toolPaletteTextBoxIds,
  ...toolPaletteShadingIds,
] as const;
export type HK_ToolPaletteMarkerId = (typeof hkToolPaletteMarkerIds)[number];
export const isHKMarkerId = (id: HK_ToolPaletteStencilId): id is HK_ToolPaletteMarkerId =>
  hkToolPaletteMarkerIds.includes(id as HK_ToolPaletteMarkerId);

/** ======== Action ======== */
/** ======== DHO ======== */
export const hkToolPaletteDHOIds = ["DHO"] as const;
export type HK_ToolPaletteDHOId = (typeof hkToolPaletteDHOIds)[number];

export const hkToolPaletteActionIds = [...hkToolPaletteDHOIds] as const;
export type HK_ToolPaletteActionId = (typeof hkToolPaletteActionIds)[number];

/** ======== All Stencils ======== */
export const hkToolPaletteStencilIds = [...hkToolPaletteMarkerIds, ...hkToolPaletteActionIds] as const;
export type HK_ToolPaletteStencilId = (typeof hkToolPaletteStencilIds)[number];
export const isStringHKToolPaletteStencilId = (id: string): id is HK_ToolPaletteStencilId =>
  hkToolPaletteStencilIds.includes(id as HK_ToolPaletteStencilId);

/** ================================
 * Tool Palette Items
 ================================ */
/** ======== Players ======== */
export const getHockeyPlayerItems = (labelType: HockeyPlayerLabelType): PlayerProp<HK_ToolPalettePlayerId>[][] => {
  const isFD = labelType === "FD";

  return [
    [
      { id: "HK_PLAYER_F_1", label: isFD ? "F1" : "LW" },
      { id: "HK_PLAYER_F_2", label: isFD ? "F2" : "C" },
      { id: "HK_PLAYER_F_3", label: isFD ? "F3" : "RW" },
      { id: "HK_PLAYER_D_1", label: isFD ? "D1" : "LD" },
      { id: "HK_PLAYER_D_2", label: isFD ? "D2" : "RD" },
      { id: "HK_PLAYER_G", label: "G" },
    ],
    [
      { id: "HK_PLAYER_POSSESSION_F_1", label: isFD ? "F1" : "LW", style: PlayerStyle.OFFSET_POSSESSION },
      { id: "HK_PLAYER_POSSESSION_F_2", label: isFD ? "F2" : "C", style: PlayerStyle.OFFSET_POSSESSION },
      { id: "HK_PLAYER_POSSESSION_F_3", label: isFD ? "F3" : "RW", style: PlayerStyle.OFFSET_POSSESSION },
      { id: "HK_PLAYER_POSSESSION_D_1", label: isFD ? "D1" : "LD", style: PlayerStyle.OFFSET_POSSESSION },
      { id: "HK_PLAYER_POSSESSION_D_2", label: isFD ? "D2" : "RD", style: PlayerStyle.OFFSET_POSSESSION },
      { id: "HK_PLAYER_POSSESSION_G", label: "G", style: PlayerStyle.OFFSET_POSSESSION },
    ],
    [
      { id: "HK_PLAYER_FILL_F_1", label: isFD ? "F1" : "LW", style: PlayerStyle.FILL },
      { id: "HK_PLAYER_FILL_F_2", label: isFD ? "F2" : "C", style: PlayerStyle.FILL },
      { id: "HK_PLAYER_FILL_F_3", label: isFD ? "F3" : "RW", style: PlayerStyle.FILL },
      { id: "HK_PLAYER_FILL_D_1", label: isFD ? "D1" : "LD", style: PlayerStyle.FILL },
      { id: "HK_PLAYER_FILL_D_2", label: isFD ? "D2" : "RD", style: PlayerStyle.FILL },
      { id: "HK_PLAYER_FILL_G", label: "G", style: PlayerStyle.FILL },
    ],
  ];
};

/** ======== Misc ======== */
export const HK_miscellaneousItems: ActionItem<HK_ToolPaletteMiscId | ToolPaletteTextBoxId | HK_ToolPaletteDHOId>[] = [
  { id: "MISC_PUCK", label: "Puck", iconUrl: ball },
  { id: "MISC_CONE", label: "Cone", iconUrl: cone },
  { id: "MISC_TEXTBOX", label: "Textbox", iconUrl: textbox },
  { id: "MISC_TIRE", label: "Tire", iconUrl: tire },
  { id: "MISC_STICK_RIGHT", label: "Stick Right", iconUrl: stickRight },
  { id: "MISC_STICK_LEFT", label: "Stick Left", iconUrl: stickLeft },
  { id: "MISC_TRIANGLE_STICK", label: "Triangle Stick", iconUrl: triangleStick },
  { id: "MISC_BOARD", label: "Board", iconUrl: board },
  { id: "MISC_PUCK_PILE", label: "Puck Pile", iconUrl: pile },
  { id: "MISC_NET", label: "Net", iconUrl: net },
];

export interface GetHockeyToolPaletteProps {
  labelType: HockeyPlayerLabelType;
  onChange: () => void;
}

export const getHockeyToolPalette = ({ labelType, onChange }: GetHockeyToolPaletteProps): AccordionItemProps[] => {
  return [
    {
      type: "TOGGLE",
      id: "positions",
      label: "Positions",
      toggle: { checked: labelType === "LR", onChange, onText: "R/L", offText: "F/D" },
      bodyRender: <Positions positions={getHockeyPlayerItems(labelType)} />,
    },
    {
      type: "STANDARD",
      id: "miscellaneous",
      label: "Miscellaneous",
      bodyRender: <IconItems items={HK_miscellaneousItems} />,
    },
    ...getGlobalToolPane(),
  ];
};
